import { Dialog, Transition } from '@headlessui/react';
import { Fragment, useEffect, useState } from 'react';
import { getAuthorizationHeader, http } from '../config/http';
import { CurrencyInput } from '../utils/CurrencyInput';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import {
  IconArrowAutofitRight,
  IconBuildingStore,
  IconArrowUpRight,
  IconArrowDownRight,
  IconCash,
  IconNotes,
  IconX,
  IconCheck,
} from '@tabler/icons-react';

export default function TambahSaldoKAS({ closeModalTambahSaldo, isOpenTambahSaldo, submitTambahSaldoKas }) {
  const [cabang_id, setCabangId] = useState('');
  const [nominal, setNominal] = useState(0);
  const [status, setStatus] = useState('');
  const [keterangan, setKeterangan] = useState('');
  const [loadingTambahSaldo, setLoadingTambahSaldo] = useState(false);
  const [cabang, setCabang] = useState([]);
  const navigate = useNavigate();

  function resetState() {
    setCabangId('');
    setNominal(0);
    setStatus('');
    setKeterangan('');
  }

  useEffect(() => {
    async function getSaldoCabang() {
      try {
        const res = await http.get(`/cabang?is_toko=1`, {
          headers: { Authorization: getAuthorizationHeader() },
        });
        setCabang(res.data.data || []);
      } catch (err) {
        if (!err.response) {
          return null;
        } else if (err.response.status === 401) {
          navigate('/login');
        }
      }
    }
    if (isOpenTambahSaldo) {
      getSaldoCabang();
    }
  }, [navigate, isOpenTambahSaldo]);

  async function handleSubmitTambahSaldo(e) {
    if (e && e.preventDefault) e.preventDefault();
    if (!cabang_id) {
      toast.error('Pilih toko terlebih dahulu!', { autoClose: 1500 });
      return;
    }
    if (!status) {
      toast.error('Pilih status transaksi (Setor / Tarik)!', { autoClose: 1500 });
      return;
    }
    if (!nominal || Number(nominal) <= 0) {
      toast.error('Masukkan nominal transaksi yang valid!', { autoClose: 1500 });
      return;
    }

    setLoadingTambahSaldo(true);
    const data = {
      cabang_id,
      nominal: Number(nominal),
      status,
      keterangan,
    };
    try {
      const response = await http.post('saldo', data, {
        headers: { Authorization: getAuthorizationHeader() },
      });
      toast.success(response.data?.message || 'Transaksi saldo berhasil disimpan!', { autoClose: 1000 });
      setLoadingTambahSaldo(false);
      closeModalTambahSaldo();
      submitTambahSaldoKas();
      resetState();
      return response.data?.data;
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || 'Gagal menyimpan transaksi saldo!', { autoClose: 2000 });
      setLoadingTambahSaldo(false);
    }
  }

  return (
    <Transition appear show={isOpenTambahSaldo} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={closeModalTambahSaldo}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-xl transform overflow-hidden rounded-2xl bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 p-6 text-left align-middle shadow-2xl transition-all">
                
                {/* Header */}
                <div className="flex items-start justify-between pb-4 border-b border-slate-100 dark:border-slate-800">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-2xl bg-amber-500/10 dark:bg-amber-400/15 border border-amber-500/20 dark:border-amber-400/30 flex items-center justify-center text-amber-600 dark:text-amber-400 shadow-inner">
                      <IconArrowAutofitRight size={22} />
                    </div>
                    <div>
                      <Dialog.Title as="h3" className="text-base sm:text-lg font-bold text-slate-800 dark:text-slate-100">
                        Setor / Tarik Saldo Kas
                      </Dialog.Title>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        Tambah setoran kas masuk atau penarikan kas keluar toko
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={closeModalTambahSaldo}
                    type="button"
                    className="p-1.5 rounded-xl text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                  >
                    <IconX size={18} />
                  </button>
                </div>

                {/* Form Body */}
                <form onSubmit={handleSubmitTambahSaldo} className="mt-5 space-y-4">
                  {/* Field 1: Pilih Status (Segmented Buttons) */}
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300 mb-2">
                      Tipe Transaksi <span className="text-rose-500">*</span>
                    </label>
                    <div className="grid grid-cols-2 gap-2.5">
                      <button
                        type="button"
                        onClick={() => setStatus('setor')}
                        className={`flex items-center justify-center gap-2 p-3 rounded-xl border text-xs sm:text-sm font-bold transition-all ${
                          status === 'setor'
                            ? 'bg-emerald-500 text-white border-emerald-500 shadow-sm shadow-emerald-500/30'
                            : 'bg-slate-50 dark:bg-slate-800/80 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-700 hover:bg-emerald-50 dark:hover:bg-emerald-950/30 hover:border-emerald-300'
                        }`}
                      >
                        <IconArrowUpRight size={18} />
                        <span>Setor Tunai (Kas Masuk)</span>
                        {status === 'setor' && <IconCheck size={16} />}
                      </button>

                      <button
                        type="button"
                        onClick={() => setStatus('tarik')}
                        className={`flex items-center justify-center gap-2 p-3 rounded-xl border text-xs sm:text-sm font-bold transition-all ${
                          status === 'tarik'
                            ? 'bg-rose-500 text-white border-rose-500 shadow-sm shadow-rose-500/30'
                            : 'bg-slate-50 dark:bg-slate-800/80 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-700 hover:bg-rose-50 dark:hover:bg-rose-950/30 hover:border-rose-300'
                        }`}
                      >
                        <IconArrowDownRight size={18} />
                        <span>Tarik Tunai (Kas Keluar)</span>
                        {status === 'tarik' && <IconCheck size={16} />}
                      </button>
                    </div>
                  </div>

                  {/* Field 2: Pilih Toko / Etalase */}
                  <div>
                    <label
                      htmlFor="cabang_id"
                      className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300 mb-1.5"
                    >
                      <IconBuildingStore size={15} className="text-amber-500" />
                      <span>Pilih Toko / Etalase</span>
                      <span className="text-rose-500">*</span>
                    </label>
                    <div className="relative">
                      <select
                        id="cabang_id"
                        name="cabang_id"
                        value={cabang_id}
                        onChange={(e) => setCabangId(e.target.value)}
                        className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/90 text-slate-800 dark:text-slate-100 text-xs sm:text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-500 transition-all cursor-pointer"
                      >
                        <option value="">-- Pilih Toko / Etalase --</option>
                        {cabang.map((cb) => (
                          <option value={cb.id} key={cb.id}>
                            {cb.nama_cabang}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Field 3: Masukkan Nominal */}
                  <div>
                    <label
                      htmlFor="nominal"
                      className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300 mb-1.5"
                    >
                      <IconCash size={15} className="text-amber-500" />
                      <span>Nominal Transaksi (Rp)</span>
                      <span className="text-rose-500">*</span>
                    </label>
                    <div className="relative">
                      <CurrencyInput
                        name="nominal"
                        value={nominal}
                        onChange={(value) => setNominal(value)}
                        className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/90 text-slate-800 dark:text-slate-100 text-sm sm:text-base font-extrabold focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-500 transition-all"
                        placeholder="0"
                      />
                    </div>
                  </div>

                  {/* Field 4: Keterangan */}
                  <div>
                    <label
                      htmlFor="message"
                      className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300 mb-1.5"
                    >
                      <IconNotes size={15} className="text-amber-500" />
                      <span>Keterangan</span>
                    </label>
                    <textarea
                      id="message"
                      value={keterangan}
                      onChange={(e) => setKeterangan(e.target.value)}
                      rows={3}
                      name="keterangan"
                      className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/90 text-slate-800 dark:text-slate-100 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-500 transition-all placeholder:text-slate-400"
                      placeholder="Catatan tambahan untuk transaksi setor/tarik saldo..."
                    />
                  </div>

                  {/* Footer Buttons */}
                  <div className="pt-3 border-t border-slate-100 dark:border-slate-800 flex items-center justify-end gap-2.5">
                    <button
                      type="button"
                      onClick={closeModalTambahSaldo}
                      className="px-5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 font-bold text-xs sm:text-sm transition-all"
                    >
                      Batal
                    </button>
                    <button
                      type="submit"
                      disabled={loadingTambahSaldo}
                      className="px-6 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-600 active:bg-amber-700 text-white font-bold text-xs sm:text-sm shadow-sm transition-all flex items-center gap-2 disabled:opacity-50 active:scale-95 cursor-pointer"
                    >
                      {loadingTambahSaldo ? (
                        <>
                          <div className="w-4 h-4 rounded-full animate-spin border-2 border-white border-t-transparent" />
                          <span>Menyimpan...</span>
                        </>
                      ) : (
                        <>
                          <IconCheck size={16} />
                          <span>Simpan Transaksi</span>
                        </>
                      )}
                    </button>
                  </div>
                </form>

              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
